import { useState, useEffect } from "react";
import { Button, Container, Typography, Box, List, ListItem, IconButton, TextField, Select, MenuItem, FormControl, InputLabel, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { TaskAlt, CheckCircle, Flag, CenterFocusStrong } from "@mui/icons-material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import quotes from "../data/quotes";

const loadActiveTaskId = () => {
  try {
    return JSON.parse(localStorage.getItem("activeTaskId")) ?? null;
  } catch {
    return null;
  }
};

const TaskManager = () => {
  const [tasks, setTasks] = useState(() => {
    // Load tasks from localStorage on initial render
    return JSON.parse(localStorage.getItem("tasks")) || [];
  });
  const [task, setTask] = useState("");
  const [priority, setPriority] = useState("medium");
  const [category, setCategory] = useState("work");
  const [estimatedPomodoros, setEstimatedPomodoros] = useState(1);
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [activeTaskId, setActiveTaskId] = useState(loadActiveTaskId);

  // Stay in sync with the Timer, which also reads/writes "tasks" and "activeTaskId".
  useEffect(() => {
    const sync = () => {
      setTasks(JSON.parse(localStorage.getItem("tasks")) || []);
      setActiveTaskId(loadActiveTaskId());
    };
    window.addEventListener("tasks-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("tasks-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setAsFocusTask = (id) => {
    const newVal = activeTaskId === id ? null : id;
    setActiveTaskId(newVal);
    localStorage.setItem("activeTaskId", JSON.stringify(newVal));
    window.dispatchEvent(new Event("tasks-updated"));
  };

  // Display a random quote using toast
  // const showRandomQuote = () => {
  //   // toast.dismiss();
  //   const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
  //   toast.info(randomQuote, {
  //     // position: "bottom-right",
  //     autoClose: 10000, 
  //     closeButton: false, // Removes the close button
  //     icon: false, // Removes the icon

  // });
    
  // };

  // Add a new task
  const addTask = () => {
    if (task.trim()) {
      const newTaskObj = {
        id: Date.now(),
        text: task,
        priority,
        category,
        estimatedPomodoros: parseInt(estimatedPomodoros),
        completed: false,
        completedPomodoros: 0,
        dateAdded: new Date().toISOString(),
        dateCompleted: null,
      };
      const updatedTasks = [...tasks, newTaskObj];
      setTasks(updatedTasks);
      localStorage.setItem("tasks", JSON.stringify(updatedTasks));
      window.dispatchEvent(new Event("tasks-updated"));
      setTask("");
      setPriority("medium");
      setCategory("work");
      setEstimatedPomodoros(1);
    }
  };

  // Remove a task
  const removeTask = (id) => {
    const updatedTasks = tasks.filter((t) => t.id !== id);
    setTasks(updatedTasks);
    localStorage.setItem("tasks", JSON.stringify(updatedTasks));
    if (activeTaskId === id) {
      setActiveTaskId(null);
      localStorage.setItem("activeTaskId", JSON.stringify(null));
    }
    window.dispatchEvent(new Event("tasks-updated"));
  };

  // Toggle task completion
  const toggleTaskCompletion = (id) => {
    const updatedTasks = tasks.map((t) =>
      t.id === id
        ? { ...t, completed: !t.completed, dateCompleted: !t.completed ? new Date().toISOString() : null }
        : t
    );
    setTasks(updatedTasks);
    localStorage.setItem("tasks", JSON.stringify(updatedTasks));
    window.dispatchEvent(new Event("tasks-updated"));
  };

  // Mark pomodoro as completed for a task
  const addCompletedPomodoro = (id) => {
    const updatedTasks = tasks.map((t) =>
      t.id === id && t.completedPomodoros < t.estimatedPomodoros
        ? { ...t, completedPomodoros: t.completedPomodoros + 1 }
        : t
    );
    setTasks(updatedTasks);
    localStorage.setItem("tasks", JSON.stringify(updatedTasks));
    window.dispatchEvent(new Event("tasks-updated"));
  };

  // Filter and sort tasks
  const filteredTasks = tasks
    .filter((t) => filterCategory === "all" || t.category === filterCategory)
    .sort((a, b) => {
      if (sortBy === "priority") {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      } else if (sortBy === "date") {
        return new Date(b.dateAdded) - new Date(a.dateAdded);
      }
      return 0;
    });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#FF5252";
      case "medium":
        return "#FFA726";
      case "low":
        return "#66BB6A";
      default:
        return "#FFA726";
    }
  };

  // // Show random quote every 5 minutes
  // useEffect(() => {
  //   showRandomQuote(); // Show immediately on load
  //   const intervalId = setInterval(showRandomQuote, (0.1) * 60 * 1000); // 5 minutes
  //   return () => clearInterval(intervalId); // Cleanup on unmount
  // }, []);

  return (
    <Container
      maxWidth="sm"
      className="task-manager-container"
      sx={{ minHeight: "5vh", display: "flex", flexDirection: "column", justifyContent: "center" }}
    >
      <Typography variant="h4" align="center" sx={{
        fontWeight: 900,
        fontFamily: "'Montserrat', sans-serif",
        background: "linear-gradient(90deg, #FF6F61, #D84315)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        textTransform: "uppercase",
        letterSpacing: "2px",
      }} gutterBottom>
        Task Manager
      </Typography>

      {/* Task Input with Priority, Category, and Estimate */}
      <Box mb={3} display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Add a new task..."
          variant="outlined"
          fullWidth
          value={task}
          onChange={(e) => setTask(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && addTask()}
          className="task-input"
        />

        <Box display="flex" gap={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
              <MenuItem value="work">Work</MenuItem>
              <MenuItem value="personal">Personal</MenuItem>
              <MenuItem value="health">Health</MenuItem>
              <MenuItem value="learning">Learning</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Pomodoros</InputLabel>
            <Select value={estimatedPomodoros} label="Pomodoros" onChange={(e) => setEstimatedPomodoros(e.target.value)}>
              {[1, 2, 3, 4, 5].map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button
          variant="contained"
          sx={{
            flex: 1,
            background: "linear-gradient(45deg, #3F51B5, #2196F3)",
            color: "#fff",
            fontWeight: "bold",
            boxShadow: "0 6px 20px rgba(63, 81, 181, 0.6)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "linear-gradient(45deg, #2196F3, #3F51B5)",
              boxShadow: "0 8px 25px rgba(33, 150, 243, 0.8)",
              transform: "scale(1.05)",
            },
            "&:active": {
              background: "linear-gradient(45deg, #2196F3, #3F51B5)",
              boxShadow: "0 4px 15px rgba(33, 150, 243, 0.6)",
              transform: "scale(0.95)",
              transition: "all 0.1s ease",
            },
            "&:focus": {
              outline: "none",
              boxShadow: "0 0 0 4px rgba(33, 150, 243, 0.3)",
            },
          }}
          color="primary"
          fullWidth
          onClick={addTask}
          startIcon={<TaskAlt />}
        >
          Add Task
        </Button>
      </Box>

      {/* Filter and Sort Controls */}
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" fullWidth>
          <InputLabel>Filter by Category</InputLabel>
          <Select value={filterCategory} label="Filter by Category" onChange={(e) => setFilterCategory(e.target.value)}>
            <MenuItem value="all">All Categories</MenuItem>
            <MenuItem value="work">Work</MenuItem>
            <MenuItem value="personal">Personal</MenuItem>
            <MenuItem value="health">Health</MenuItem>
            <MenuItem value="learning">Learning</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Sort By</InputLabel>
          <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="date">Date Added</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Task List */}
      <List>
        {filteredTasks.map((t) => (
          <ListItem
            key={t.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              padding: "12px",
              border: `2px solid ${getPriorityColor(t.priority)}`,
              borderRadius: "8px",
              backgroundColor: t.completed
                ? "rgba(100, 150, 100, 0.1)"
                : activeTaskId === t.id
                ? "rgba(255, 64, 129, 0.08)"
                : "transparent",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Box display="flex" flexDirection="column" flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  sx={{
                    textDecoration: t.completed ? "line-through" : "none",
                    opacity: t.completed ? 0.6 : 1,
                    fontWeight: "500",
                  }}
                >
                  {t.text}
                </Typography>
                <Chip
                  label={t.category}
                  size="small"
                  sx={{ backgroundColor: getPriorityColor(t.priority), color: "#fff" }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Chip
                  icon={<Flag />}
                  label={`${t.priority} priority`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${t.completedPomodoros}/${t.estimatedPomodoros} pomodoros`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>

            <Box display="flex" gap={1}>
              {!t.completed && (
                <IconButton
                  size="small"
                  title="Focus this task in the Timer"
                  onClick={() => setAsFocusTask(t.id)}
                >
                  <CenterFocusStrong sx={{ color: activeTaskId === t.id ? "#FF4081" : "#ccc" }} />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => toggleTaskCompletion(t.id)}>
                <CheckCircle sx={{ color: t.completed ? "#66BB6A" : "#ccc" }} />
              </IconButton>
              {!t.completed && t.completedPomodoros < t.estimatedPomodoros && (
                <Button size="small" onClick={() => addCompletedPomodoro(t.id)}>
                  +Pomo
                </Button>
              )}
              <IconButton size="small" onClick={() => removeTask(t.id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </ListItem>
        ))}
      </List>

      <ToastContainer />
    </Container>
  );
};

export default TaskManager;
